import history from '@history';
import { setInitialSettings } from 'app/store/actions/fuse';
import jwtService from './jwtService';
import axios from 'axios';
import { showMessage } from 'app/store/actions/fuse';
import { API_BASE_URL } from '@config';
import { saveAs } from 'file-saver';
import store from 'app/store';
import * as ActionsDialog from 'app/store/actions';
import decode from 'jwt-decode';

export const CLEAR_AUTH = 'CLEAR_AUTH ';
export const clearAuth = () => ({
	type: CLEAR_AUTH
});

export const USER_LOGGED_OUT = 'USER_LOGGED_OUT';
export const userLoggedOut = () => ({
	type: USER_LOGGED_OUT
});

export const CLEAR_ERROR = 'CLEAR_ERROR';
export const clearError = () => ({
	type: CLEAR_ERROR
});

export const SET_USERS = 'SET_USERS';
export const setUsers = data => ({
	type: SET_USERS,
	payload: data
});

export const SET_ERROR = 'SET_ERROR';
export const setError = data => ({
	type: SET_ERROR,
	payload: data
});

export const SET_ACTIVITIES = 'SET_ACTIVITIES';
export const setActivities = data => ({
	type: SET_ACTIVITIES,
	payload: data
});

export const SET_USER_DATA = 'SET_USER_DATA';
export const setUserData = data => ({
	type: SET_USER_DATA,
	payload: data
});

// Logout
export const logoutUser = () => (dispatch, getState) => {
	const { role } = getState().auth.user;
	if (!role || role.length === 0) return null;

	// First, notify backend to invalidate the session
	const token = window.localStorage.getItem('token');
	if (token) {
		axios.post(`${API_BASE_URL}/api/sessions/invalidate`, {}, {
			headers: {
				Authorization: `Bearer ${token}`
			}
		})
		.then(() => {
			// On successful invalidation or if it fails, still continue with local logout
			console.log('Session invalidated successfully');
		})
		.catch(error => {
			console.error('Error invalidating session:', error);
		})
		.finally(() => {
			// Always continue with the logout process
			history.push({ pathname: '/' });
			jwtService.logout();
			dispatch(setInitialSettings());
			dispatch(clearAuth());
			dispatch(userLoggedOut());
		});
	} else {
		// No token, just proceed with normal logout
		history.push({ pathname: '/' });
		jwtService.logout();
		dispatch(setInitialSettings());
		dispatch(clearAuth());
		dispatch(userLoggedOut());
	}
};

export const downloadUserSSHKey = (targetUsername, fromClusterView = false) => dispatch => {
	const currentUsername = store.getState().auth.user.username;
	const downloadUsername = targetUsername || currentUsername;

	const params = new URLSearchParams();
	if (downloadUsername !== currentUsername) params.set('username', downloadUsername);
	if (fromClusterView) params.set('fromClusterView', 'true');
	const qs = params.toString();

	const url = `${API_BASE_URL}/api/users/downloadUserSSHKey${qs ? `?${qs}` : ''}`;
	const request = axios.get(url, { responseType: 'blob' });
	return request
		.then(res => saveAs(res.data, `${downloadUsername}-SSH-Key.pem`))
		.catch(err => dispatch(setError({ ...err.data, type: 'modal' })));
};

export const participantDownloadSSHKey = () => dispatch => {
	const username = store.getState().auth.user.username;

	const url = `${API_BASE_URL}/api/users/participant/ssh-key`;
	const request = axios.get(url, { responseType: 'blob' });
	return request
		.then(res => saveAs(res.data, `${username}-SSH-Key.pem`))
		.catch(err => dispatch(setError({ ...err.data, type: 'modal' })));
};

export const getUserActivities = () => dispatch => {
	const url = `${API_BASE_URL}/api/users/activities/`;
	const request = axios.get(url);
	return request
		.then(res => dispatch(setActivities(res.data)))
		.catch(err => dispatch(setError({ ...err.data, type: 'modal' })));
};

export const getUsers = () => dispatch => {
	const url = `${API_BASE_URL}/api/users/`;
	const request = axios.get(url);
	return request
		.then(res => dispatch(setUsers(res.data)))
		.catch(err => dispatch(setError({ ...err.data, type: 'modal' })));
};

export const recoverPassword = email => dispatch => {
	const request = axios.post(`${API_BASE_URL}/api/password-reset/request`, email);
	return request
		.then(() => dispatch(showMessage({ message: 'If an account exists with this email, you will receive a password reset link.' })))
		.catch(err => dispatch(setError({ ...err.data, type: 'modal' })));
};

export const verifyResetToken = token => () => {
	return axios.post(`${API_BASE_URL}/api/password-reset/verify`, { token });
};

export const resetPassword = data => dispatch => {
	const request = axios.post(`${API_BASE_URL}/api/password-reset/reset`, data);
	return request
		.then(() => dispatch(showMessage({ message: 'Password Reset Successful' })))
		.catch(err => dispatch(setError({ ...err.data, type: 'modal' })));
};

export const updateUser = data => dispatch => {
	dispatch(ActionsDialog.toggleLoading());
	const token = window.localStorage.getItem('token');
	const currentUser = decode(token).user;
	const isSelf = currentUser.id === data.id;

	const request = axios.put(`${API_BASE_URL}/api/users/`, data);
	return request
		.then(() => {
			dispatch(ActionsDialog.toggleLoading());
			dispatch(showMessage({ message: 'User Updated' }));
			dispatch(getUsers());

			// Only force logout when auth-critical fields changed on self
			if (isSelf && (data.role !== currentUser.role || data.organizationId !== currentUser.organizationId)) {
				dispatch(logoutUser());
			}
		})
		.catch(err => {
			dispatch(ActionsDialog.toggleLoading());
			dispatch(setError({ ...err.data, type: 'modal' }));
		});
};

export const updateUserSelf = data => dispatch => {
	dispatch(ActionsDialog.toggleLoading());
	const request = axios.put(`${API_BASE_URL}/api/users/`, data);
	return request
		.then(() => {
			dispatch(ActionsDialog.toggleLoading());
			dispatch(showMessage({ message: 'User Updated, you must log in again to see the changes' }));
		})
		.catch(err => {
			dispatch(ActionsDialog.toggleLoading());
			dispatch(setError({ ...err.data, type: 'modal' }));
		});
};

export const changePassword = data => dispatch => {
	dispatch(ActionsDialog.toggleLoading());
	const request = axios.put(`${API_BASE_URL}/api/users/changePassword`, data);
	return request
		.then(() => {
			dispatch(ActionsDialog.toggleLoading());
			dispatch(ActionsDialog.closeDialog());
			dispatch(showMessage({ message: 'Password Updated' }));
		})
		.catch(err => {
			dispatch(ActionsDialog.toggleLoading());
			dispatch(setError({ ...err.data, type: 'form' }));
		});
};

export const deleteUser = data => dispatch => {
	dispatch(ActionsDialog.toggleLoading());
	const request = axios.put(`${API_BASE_URL}/api/users/remove`, data);
	return request
		.then(() => {
			dispatch(ActionsDialog.toggleLoading());
			dispatch(ActionsDialog.closeDialog());
			dispatch(showMessage({ message: 'Deleted User' }));
			dispatch(getUsers());
		})
		.catch(err => {
			dispatch(ActionsDialog.toggleLoading());
			dispatch(setError({ ...err.data, type: 'form' }));
		});
};
